import React, { useEffect, useState } from "react";
import axios from "axios";
import {PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Bar, BarChart, XAxis, YAxis} from "recharts";
import { saveAs } from "file-saver";
import "./UsersData.css";

const UsersData = () => {
    const [users, setUsers] = useState([]);
    const [activity, setActivity] = useState([]);
    const [filteredActivity, setFilteredActivity] = useState([]);
    const [dateRange, setDateRange] = useState({ start: "", end: "" });
    const [searchPage, setSearchPage] = useState("");
    const [userJourney, setUserJourney] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 10;
    const [activityHeatmap, setActivityHeatmap] = useState({});
    const [messages, setMessages] = useState([]);

    useEffect(() => {
        fetchUsers();
        fetchUserActivity();
    }, []);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    const [mostActiveUsers, setMostActiveUsers] = useState([]);

    useEffect(() => {
        const userActivityCounts = {};
        activity.forEach(log => {
            if (log.user) {
                userActivityCounts[log.user] = (userActivityCounts[log.user] || 0) + 1;
            }
        });

        const sortedUsers = Object.entries(userActivityCounts)
            .map(([user, count]) => ({ user, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        setMostActiveUsers(sortedUsers);
    }, [activity]);
    useEffect(() => {
        const heatmapData = Array(24).fill(0);
        activity.filter(log => !log.page.startsWith("/admin")).forEach(log => {
            const logHour = new Date(log.timestamp).getHours();
            heatmapData[logHour]++;
        });
        setActivityHeatmap(heatmapData);
    }, [activity]);
    useEffect(() => {
        const journeyData = {};

        activity.forEach((log, index, arr) => {
            if (index === 0) return;

            const prevLog = arr[index - 1];
            if (!prevLog.page.startsWith("/admin") && !log.page.startsWith("/admin")) {
                const journeyKey = `${prevLog.page} → ${log.page}`;
                journeyData[journeyKey] = (journeyData[journeyKey] || 0) + 1;
            }
        });
        setUserJourney(Object.entries(journeyData).map(([path, count]) => ({ path, count })));
    }, [activity]);
    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`);
            setUsers(res.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };
    const fetchUserActivity = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/activity`);
            const filteredData = res.data.filter(log => !log.page.startsWith("/admin"));

            setActivity(filteredData);
            setFilteredActivity(filteredData);
        } catch (error) {
            console.error("Error fetching user activity:", error);
        }
    };

    useEffect(() => {
        const filtered = activity.filter(log => {
            const logDate = new Date(log.timestamp);
            const startDate = dateRange.start ? new Date(dateRange.start) : null;
            const endDate = dateRange.end ? new Date(dateRange.end) : null;
            const isWithinDateRange =
                (!startDate || logDate >= startDate) &&
                (!endDate || logDate <= endDate);
            const matchesSearch = searchPage === "" || log.page.toLowerCase().includes(searchPage.toLowerCase());
            return isWithinDateRange && matchesSearch;
        });

        setFilteredActivity(filtered);
    }, [dateRange, searchPage, activity]);
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredActivity.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredActivity.length / recordsPerPage);


    useEffect(() => {
        fetchMessages();
    }, []);
    const fetchMessages = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/messages`);
            const sortedMessages = res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setMessages(sortedMessages);
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };
    const goToPage = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };
    const pageVisitCounts = {};
    filteredActivity.forEach(log => {
        if (log.page) {
            pageVisitCounts[log.page] = (pageVisitCounts[log.page] || 0) + 1;
        }
    });

    const pieChartData = Object.entries(pageVisitCounts)
        .filter(([page]) =>
            page === "/home" ||
            page === "/about" ||
            page === "/contact" ||
            page.startsWith("/event/") ||
            page === "/checkout" ||
            page === "/ticket-check"
        )
        .map(([page, count]) => ({
            name: page,
            value: count
        }));


    const exportToCSV = () => {
        const csvRows = [
            ["User", "Page Visited", "Timestamp"],
            ...filteredActivity.map(log => [
                log.user,
                log.page,
                new Date(log.timestamp).toLocaleString()
            ])
        ];
        const csvContent = csvRows.map(row => row.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        saveAs(blob, "user_activity.csv");
    };

    return (
        <div className="users-data-container">
            <h1>User Data & Activity</h1>
            <h2>Registered Users</h2>
            <div className="table-container">
                <table className="users-table">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Role</th>
                    </tr>
                    </thead>
                    <tbody>
                    {users.map(user => (
                        <tr key={user._id}>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.phone}</td>
                            <td>{user.role}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <h2>User Activity</h2>
            <div className="filters">
                <label>Start Date:</label>
                <input type="date" value={dateRange.start} onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))} />
                <label>End Date:</label>
                <input type="date" value={dateRange.end} onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))} />
                <label>Search Page:</label>
                <input type="text" placeholder="Enter page name" value={searchPage} onChange={(e) => setSearchPage(e.target.value)} />
                <button className="export-button" onClick={exportToCSV}>Export CSV</button>
            </div>
            <div className="table-container">
                <table className="activity-table">
                    <thead>
                    <tr>
                        <th>User</th>
                        <th>Page Visited</th>
                        <th>Timestamp</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentRecords.map((log, index) => (
                        <tr key={index}>
                            <td>{log.user}</td>
                            <td>{log.page}</td>
                            <td>{new Date(log.timestamp).toLocaleString()}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <div className="pagination">
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                <span> Page {currentPage} of {totalPages} </span>
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
            </div>
            <h2>Most Active Users</h2>
            <div className="table-container">
                <table className="active-users-table">
                    <thead>
                    <tr>
                        <th>User</th>
                        <th>Activity Count</th>
                    </tr>
                    </thead>
                    <tbody>
                    {mostActiveUsers.map((user, index) => (
                        <tr key={index}>
                            <td>{user.user}</td>
                            <td>{user.count}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <h2>Activity Heatmap</h2>
            <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={Object.entries(activityHeatmap).map(([hour, count]) => ({ hour, count }))}>
                        <XAxis dataKey="hour" tickFormatter={(tick) => `${tick}:00`} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#007bff" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <h2>User Journey Flow</h2>
            <div className="chart-containerBarChart">
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={userJourney}>
                        <XAxis dataKey="path" tick={{ angle: -25, textAnchor: "end" }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#28a745" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="pie-chart-container">
                <h2>Most Visited Pages</h2>
                {pieChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                            <Pie data={pieChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={170} fill="#007bff" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                {pieChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={["#007bff", "#28a745", "#dc3545", "#ffc107", "#17a2b8"][index % 5]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <p>No activity data available</p>
                )}
            </div>
            <h2>Contact Messages</h2>
            <div className="table-container">
                <table className="messages-table">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Message</th>
                        <th>Date</th>
                    </tr>
                    </thead>
                    <tbody>
                    {messages.map((msg, index) => (
                        <tr key={index}>
                            <td>{msg.user}</td>
                            <td>{msg.email}</td>
                            <td>{msg.phone && msg.phone.trim() !== "" ? msg.phone : "N/A"}</td>
                            <td>{msg.message}</td>
                            <td>{new Date(msg.createdAt).toLocaleString()}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default UsersData;
